import { useEffect, useRef, useState } from 'react'
import { Orb } from '@/components/ui/orb'

// Drives the ElevenLabs orb from the WebRTC remote audio stream attached to
// the page's <audio> element. Reads time-domain samples each frame, computes
// an RMS-ish level, and exposes it as outputVolumeRef so the orb pulses with
// the bot's voice. agentState toggles the orb's listening/talking visual.
export default function AudioOrb({ audioRef, active }) {
  const outputVolumeRef = useRef(0)
  const inputVolumeRef = useRef(0)
  const [agentState, setAgentState] = useState('listening')

  useEffect(() => {
    if (!active) return
    let cancelled = false
    let ctx = null
    let analyser = null
    let source = null
    let buf = null
    let raf = 0
    let retry = null

    const attach = () => {
      if (cancelled) return
      const audio = audioRef?.current
      const stream = audio?.srcObject
      if (!stream || stream.getAudioTracks?.().length === 0) {
        retry = setTimeout(attach, 250)
        return
      }
      try {
        const Ctor = window.AudioContext || window.webkitAudioContext
        ctx = new Ctor()
        source = ctx.createMediaStreamSource(stream)
        analyser = ctx.createAnalyser()
        analyser.fftSize = 1024
        analyser.smoothingTimeConstant = 0.85
        source.connect(analyser)
        // Do NOT connect to ctx.destination — the <audio> element is already
        // playing the same stream.
        buf = new Uint8Array(analyser.fftSize)
        const tick = () => {
          if (cancelled) return
          analyser.getByteTimeDomainData(buf)
          let sum = 0
          for (let i = 0; i < buf.length; i++) {
            const v = (buf[i] - 128) / 128
            sum += v * v
          }
          const rms = Math.sqrt(sum / buf.length)
          const level = Math.min(1, rms * 3.5)
          outputVolumeRef.current = level
          setAgentState((current) => {
            const next = level > 0.04 ? 'talking' : 'listening'
            return current === next ? current : next
          })
          raf = requestAnimationFrame(tick)
        }
        tick()
      } catch (e) {
        console.warn('AudioOrb: analyser attach failed:', e)
      }
    }

    attach()

    return () => {
      cancelled = true
      if (retry) clearTimeout(retry)
      if (raf) cancelAnimationFrame(raf)
      try { source?.disconnect?.() } catch (_) { /* noop */ }
      if (ctx && ctx.state !== 'closed') ctx.close().catch(() => {})
    }
  }, [active, audioRef])

  if (!active) return null

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-950 via-sky-950 to-slate-950">
      <div className="relative h-3/4 w-3/4 max-w-[420px]">
        <Orb
          volumeMode="manual"
          agentState={agentState}
          outputVolumeRef={outputVolumeRef}
          inputVolumeRef={inputVolumeRef}
          colors={["#7dd3fc", "#0284c7"]}
        />
      </div>
      <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs font-medium text-white/85 backdrop-blur">
        Audio call
      </div>
    </div>
  )
}
